import { type FormikErrors, type FormikTouched, useFormik } from 'formik'
import { useEffect, useRef, useState } from 'react'
import * as Yup from 'yup'

import { DatePicker, DateHelper } from '@loadsmart/loadsmart-ui'
import { Drawer, Field, Icon, Layout, Steps } from '@loadsmart/miranda-react'

import { RequestStatus } from 'core/domain/Request'

import { type DockAvailability, type ScheduleFormValues } from '../../domain/Shipment'
import { getStartTimesFromDocks } from '../../presenters/openDockAppointmentPresenter'
import { useGetOpenDockWarehouseAvailability } from '../hooks/useGetOpenDockWarehouseAvailability'
import { ScheduleTimeList } from '../ScheduleTimeList/ScheduleTimeList'
import { ErrorMessage } from './ErrorMessage'
import { useOpenDockDrawer } from './OpenDockDrawerContext'
import { RectangleSkeleton } from './RectangleSkeleton'

//change some string values to shape
const Schema = Yup.object().shape({
  appointmentDate: Yup.string()
    .required('Required')
    .test('is-not-in-past', 'Date cannot be in the past', (value?: string) => {
      if (!value) return false

      const selectedDate = new Date(value)
      const today = new Date()

      // set same hour to compare only date fragment
      selectedDate.setHours(0, 0, 0, 0)
      today.setHours(0, 0, 0, 0)

      return selectedDate >= today
    }),
  appointmentTime: Yup.string().required('Required'),
})

const TODAY = DateHelper()

function findDockByTime(docks: DockAvailability[], time: string): string | undefined {
  const matchingDocks = docks.filter(dock => dock.startTimes.includes(time))
  if (matchingDocks.length === 0) return undefined

  return matchingDocks.reduce((lowest, dock) => (dock.sortOrder < lowest.sortOrder ? dock : lowest))
    .dockId
}

function getValidationStatus(
  fieldName: keyof ScheduleFormValues,
  errors: FormikErrors<ScheduleFormValues>,
  touched: FormikTouched<ScheduleFormValues>
) {
  if (touched[fieldName] && errors[fieldName]) {
    return 'danger'
  }

  return 'default'
}

export function ScheduleDateAndTime() {
  const { shipmentUUID, stopIndex, setStep, fields, setStaticFields, facilityTimezone } =
    useOpenDockDrawer()

  const dialogBodyRef = useRef<React.ComponentRef<typeof Drawer.Body>>(null)

  const [warehouseAvailability, setWarehouseAvailability] = useState<string[]>([])
  const [allDocksAvailability, setAllDocksAvailability] = useState<DockAvailability[]>([])
  const allowCarrierDockSelection = fields.staticFields.loadType?.allowCarrierDockSelection ?? false

  const { handleChange, values, setFieldValue, errors, touched, setFieldTouched, isValid } =
    useFormik<Partial<ScheduleFormValues>>({
      initialValues: {
        appointmentDate: fields.staticFields.appointmentDate,
        appointmentTime: fields.staticFields.appointmentTime,
      },
      onSubmit: () => undefined,
      validationSchema: Schema,
      validateOnMount: true,
    })

  const {
    warehouseAvailability: warehouseDocksAvailabilityResponse,
    fetchOpenDockWarehouseAvailability,
    status: warehouseAvailabilityRequestStatus,
  } = useGetOpenDockWarehouseAvailability()

  function fetchAvailability(appointmentDate: string) {
    if (fields.staticFields.loadType?.id) {
      fetchOpenDockWarehouseAvailability(shipmentUUID, stopIndex, {
        loadTypeId: fields.staticFields.loadType!.id,
        appointmentDate,
        facilityTimezone,
      })
    }
  }

  useEffect(() => {
    if (warehouseDocksAvailabilityResponse) {
      const docks = warehouseDocksAvailabilityResponse
      setAllDocksAvailability(docks)
      setWarehouseAvailability(
        getStartTimesFromDocks(
          docks,
          allowCarrierDockSelection ? fields.staticFields.dock?.value : undefined
        )
      )
    }
  }, [
    warehouseDocksAvailabilityResponse,
    allowCarrierDockSelection,
    fields.staticFields.dock?.value,
  ])

  // Re-fetch availability on mount so data is restored when the user navigates back to this screen
  useEffect(() => {
    if (values.appointmentDate) {
      fetchAvailability(values.appointmentDate)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isLoadingAvailability = warehouseAvailabilityRequestStatus === RequestStatus.Pending

  const showScheduleTimeList = Boolean(
    fields.staticFields.loadType?.id &&
      fields.staticFields.dock?.value &&
      values.appointmentDate &&
      warehouseAvailabilityRequestStatus === RequestStatus.Done
  )

  useEffect(() => {
    if (dialogBodyRef.current && warehouseAvailabilityRequestStatus === RequestStatus.Done) {
      const frameId = requestAnimationFrame(() => {
        dialogBodyRef.current?.scrollBy({
          top: 1000,
          left: 0,
          behavior: 'smooth',
        })
      })

      return () => cancelAnimationFrame(frameId)
    }
  }, [dialogBodyRef, warehouseAvailabilityRequestStatus])

  return (
    <>
      <Drawer.Body ref={dialogBodyRef}>
        <Layout.Stack gap="spacing-4" paddingB="spacing-2">
          <Steps activeStep="schedule" style={{ gap: '0' }}>
            <Steps.Step name="schedule">Schedule</Steps.Step>
            <Steps.Step name="send-info">Send Info</Steps.Step>
          </Steps>
          <Layout.Stack gap="spacing-4">
            <Field required status={getValidationStatus('appointmentDate', errors, touched)}>
              <Field.Label>Appointment date</Field.Label>
              <DatePicker
                name="appointmentDate"
                value={values.appointmentDate}
                onChange={event => {
                  setFieldTouched('appointmentDate')
                  setFieldValue('appointmentTime', null)
                  handleChange(event)

                  const appointmentDate = event.target.value
                  if (appointmentDate) {
                    fetchAvailability(appointmentDate)
                  }
                }}
                constraints={[[null, TODAY.subtract('day', 1).toString()]]}
              />
              <ErrorMessage
                validationStatus={getValidationStatus('appointmentDate', errors, touched)}
                errors={errors}
                fieldName="appointmentDate"
              />
            </Field>

            {isLoadingAvailability && <RectangleSkeleton />}
            {showScheduleTimeList && (
              <ScheduleTimeList
                timeSlots={warehouseAvailability}
                setValue={(value: string | null) => {
                  setFieldTouched('appointmentTime')
                  setFieldValue('appointmentTime', value)

                  if (!allowCarrierDockSelection && value) {
                    const dockId = findDockByTime(allDocksAvailability, value)
                    const dock = fields.staticFields.loadType?.docks.find(d => d.id === dockId)
                    if (dock) {
                      setStaticFields({ dock: { value: dock.id, label: dock.name } })
                    }
                  }
                }}
                value={values.appointmentTime}
                validationStatus={getValidationStatus('appointmentTime', errors, touched)}
                errorMessage={errors.appointmentTime}
              />
            )}
          </Layout.Stack>
        </Layout.Stack>
      </Drawer.Body>
      <Drawer.Actions>
        <Drawer.ActionTertiary
          onClick={() => setStep('schedule')}
          leading={
            <Icon
              name="arrow-left"
              color={isLoadingAvailability ? 'color-text-disabled' : 'color-text-primary'}
            />
          }
          disabled={isLoadingAvailability}
        >
          Back
        </Drawer.ActionTertiary>
        <Drawer.ActionPrimary
          disabled={!isValid}
          trailing={<Icon name="arrow-right" color="color-text-inverted" />}
          onClick={() => {
            setStep('send_info')
            setStaticFields(values)
          }}
        >
          Next
        </Drawer.ActionPrimary>
      </Drawer.Actions>
    </>
  )
}
